import React from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * Reusable BarChart component.
 *
 * Props:
 *  - labels: string[]
 *  - datasets: Array<{
 *      label: string,
 *      data: number[],
 *      backgroundColor?: string | string[],
 *      borderRadius?: number,
 *    }>
 *  - height?: number  (default 250)
 *  - horizontal?: boolean
 *  - stacked?: boolean
 *  - showLegend?: boolean
 *  - yAxisLabel?: string
 */
const BarChart = ({
    labels = [],
    datasets = [],
    height = 250,
    horizontal = false,
    stacked = false,
    showLegend = false,
    yAxisLabel,
}) => {
    const data = {
        labels,
        datasets: datasets.map((ds) => ({
            borderRadius: 6,
            borderSkipped: false,
            barThickness: "flex",
            maxBarThickness: 48,
            ...ds,
        })),
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: horizontal ? "y" : "x",
        interaction: { mode: "index", intersect: false },
        plugins: {
            legend: {
                display: showLegend,
                position: "top",
                align: "end",
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                    borderRadius: 3,
                    useBorderRadius: true,
                    padding: 16,
                    font: { size: 11, family: "'Inter', sans-serif" },
                    color: "#6B7280",
                },
            },
            tooltip: {
                backgroundColor: "#1F2937",
                titleFont: { size: 12, family: "'Inter', sans-serif" },
                bodyFont: { size: 11, family: "'Inter', sans-serif" },
                padding: 10,
                cornerRadius: 8,
            },
        },
        scales: {
            x: {
                stacked,
                grid: { display: false },
                border: { display: false },
                ticks: {
                    font: { size: 11, family: "'Inter', sans-serif" },
                    color: "#9CA3AF",
                },
            },
            y: {
                stacked,
                grid: { color: "#F3F4F6" },
                border: { display: false },
                min: 0,
                ticks: {
                    precision: 0,
                    font: { size: 11, family: "'Inter', sans-serif" },
                    color: "#9CA3AF",
                },
                title: yAxisLabel
                    ? { display: true, text: yAxisLabel, color: "#9CA3AF", font: { size: 10 } }
                    : { display: false },
            },
        },
    };

    return (
        <div style={{ height: typeof height === 'number' ? `${height}px` : height, width: "100%", position: "relative" }}>
            <Bar data={data} options={options} />
        </div>
    );
};

export default BarChart;
